import { widget } from '../../widgets/widget-decorator';
import { BindingEngine, customElement, useView, PLATFORM, TaskQueue } from 'aurelia-framework';
import { InstrumentType, InstrumentValuesReportViewModel } from '../../clients/reporting-clients';
import { WidgetBase } from '../widget-base';
import { CustomerService } from '../../services/customer-service';
import { IApplicationFilter } from '../../interfaces/i-application-filter';
import { GridStack, GridStackItem } from 'aurelia-gridstack';
import { PortfolioReportCache } from '../../services/portfolio-report-cache';
import { WidgetPropertyType } from '../widget-property-type';
import { InstrumentTypesCache } from '../../services/instrument-types-cache';
import { property } from '../property-decorator';

declare module '../../clients/reporting-clients' {
  interface InstrumentValuesReportViewModel {
    estimatedProfitLossPercent?: number;
    estimatedValuePercent?: number;
  }
}

@customElement('instrument-info-widget')
@useView(PLATFORM.moduleName('./instrument-info-widget.html'))
@widget('Instrument information', true, true)
export class InstrumentInfoWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private portfolioReportCache: PortfolioReportCache, bindingEngine: BindingEngine, private gridStackItem: GridStackItem,
    private taskQueue: TaskQueue, private gridStack: GridStack, private instrumentTypesCache: InstrumentTypesCache) {
    super(element, bindingEngine, customerService);
  }

  data: InstrumentValuesReportViewModel[];
  widgetDiv: HTMLDivElement;

  @property('Data', undefined, WidgetPropertyType.List)
  instrumentType: InstrumentType = InstrumentType.Stocks;
  async instrumentTypeListValues() {
    return [
      { value: undefined, label: 'All' },
      ... (await this.instrumentTypesCache.instrumentTypes).map(x => ({ value: x.id, label: x.name }))
    ];
  }

  @property()
  headerFont?: string;

  @property()
  headerFontSize?: number;

  @property()
  headerColor?: string;

  @property('Text')
  font?: string;

  @property('Text')
  fontSize?: number;

  @property('Text')
  color?: string;

  override async loadData(filter: IApplicationFilter) {
    return this.portfolioReportCache.instrumentValues(filter.customer.companyId, undefined, filter.dates.toStr, undefined, undefined, false);
  }

  override async setData(data: InstrumentValuesReportViewModel[]) {
    this.data = data.filter(x => this.instrumentType === undefined || x.instrumentTypeId === this.instrumentType).sort((a, b) => b.profitLossClosingBalance - a.profitLossClosingBalance);
    const { estimatedProfitLossSum, estimatedValueSum } = data.reduce((acc, curr) => {
      return {
        estimatedProfitLossSum: acc.estimatedProfitLossSum + curr.profitLossClosingBalance,
        estimatedValueSum: acc.estimatedValueSum + curr.estimatedValueClosingBalance
      };
    }, { estimatedProfitLossSum: 0, estimatedValueSum: 0 });
    this.data.forEach(x => {
      x.estimatedProfitLossPercent = x.profitLossClosingBalance / estimatedProfitLossSum * 100;
      x.estimatedValuePercent = x.estimatedValueClosingBalance / estimatedValueSum * 100;
    });
    if (!this.gridStackItem.root.classList.contains('grid-stack-item--edited') && this.gridStack.grid) {
      this.taskQueue.queueTask(() => {
        if (this.gridStackItem.root.clientHeight < this.widgetDiv.scrollHeight) {
          // set slightly larger so that there is a gap between this widget and the next one when the height is decreased
          this.gridStackItem.h += Math.ceil((this.widgetDiv.scrollHeight - this.gridStackItem.root.clientHeight) / (this.gridStack.grid.opts.cellHeight as number)) + 2;
          this.taskQueue.queueTask(() => this.gridStackItem.h -= 2);
        }
      });
    }
  }

  override configChanged(key: keyof InstrumentInfoWidget) {
    switch (key) {
      case 'instrumentType':
        this.refresh$.next();
        break;
      default:
        break;
    }
  }
}
